import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import MasterViewPage from "../common/MasterViewPage";
import StatusBadge from "../common/StatusBadge";
import { getDistricts } from "../../services/districtService";
import { getSubDivisionById } from "../../services/subDivisionService";
import {
  buildLookupMap,
  extractApiErrorMessage,
  formatMasterDate,
} from "../../utils/masterHelpers";
import "./SubDivisions.css";

export default function SubDivisionView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [districtLookup, setDistrictLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [subDivision, setSubDivision] = useState(null);
  const [toast, setToast] = useState({ message: "", open: false, severity: "error" });

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      if (!id) {
        setToast({
          message: "Sub-division ID is missing.",
          open: true,
          severity: "error",
        });
        setSubDivision(null);
        return;
      }

      const [districts, selectedSubDivision] = await Promise.all([
        getDistricts(),
        getSubDivisionById(id),
      ]);

      setDistrictLookup(buildLookupMap(districts));

      if (!selectedSubDivision) {
        setToast({
          message: "Sub-division not found.",
          open: true,
          severity: "error",
        });
      }

      setSubDivision(selectedSubDivision || null);
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          "Failed to load sub-division details."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fields = subDivision
    ? [
        { label: "Code", value: subDivision.code || "NA" },
        { label: "Name", value: subDivision.name || "NA" },
        {
          label: "District",
          value: districtLookup[subDivision.district_id] || "NA",
        },
        // { label: "District ID", value: subDivision.district_id || "NA" },
        { label: "Status", value: <StatusBadge value={subDivision.is_active} /> },
        {
          label: "Created At",
          value: formatMasterDate(subDivision.created_at),
        },
      ]
    : [];

  return (
    <MasterShell pageClassName="sub-divisions-module">
      {loading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading sub-division</h2>
          <p className="masters-state__text">
            Please wait while the sub-division details are being loaded.
          </p>
        </div>
      ) : subDivision ? (
        <MasterViewPage
          description="Sub-division details with district mapping and status indicators."
          fields={fields}
          onBack={() => navigate("/sub-divisions")}
          title="View Sub-division"
        />
      ) : (
        <div className="masters-state">
          <h2 className="masters-state__title">Sub-division not found</h2>
          <p className="masters-state__text">
            The selected sub-division could not be found in the current response.
          </p>
        </div>
      )}

      <MasterToast
        message={toast.message}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
